import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';

import {Routing} from 'nutshell-core/routing';
import type {Delay} from 'nutshell-core/types';
import {sortByPosition} from 'nutshell-core/utils/sorting/sort-by-position';

import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {EmailEditor, PLACEHOLDER_ACTION_INFO} from 'shells/email-editor/email-editor';
import {TextfieldWithCheckbox} from 'shells/form/textfield-with-checkbox';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
// @ts-expect-error - imported from .js/.jsx file
import {LinkClickIcon} from 'shells/icon/svg/link-click-icon';
// @ts-expect-error - imported from .js/.jsx file
import {SentEmailIcon} from 'shells/icon/svg/sent-email-icon';
// @ts-expect-error - imported from .js/.jsx file
import {UpgradeArrowIcon} from 'shells/icon/svg/upgrade-arrow-icon';
import {UpgradeArrow} from 'shells/limits/upgrade-arrow';
import {ListSequence} from 'shells/list-sequence/list-sequence';
import {ListSequenceRowBody} from 'shells/list-sequence/list-sequence-row-body';
import {SelectableListSingle} from 'shells/selectable-list/selectable-list';
import {SectionLabel} from 'shells/typography/section-label';

import {GetSessionUser} from '../../core/get-session-user';

import {hasEmailSequenceAutomation} from '../has-email-sequence-automation';
import {hasEmailSequenceCapability} from '../has-email-sequence-capability';
import {EmailDelay} from '../sender/email-delay';

import {countHtmlAnchorElements} from './count-html-anchor-elements';
import {PLACEHOLDER_ID} from './create-empty-sequence-template';
import type {EmailTemplate} from './types';

import styles from './email-template-designer-editor.module.css';

const NO_SUBJECT = 'No subject';
const NO_BODY = 'No email body';

type Props = {
    isLoading: boolean;
    readOnly?: boolean;
    selectedTemplateId: string;
    emailTemplates: {
        [templateId: string]: EmailTemplate;
    };
    sortedEmailSequenceTemplates: {
        id: string;
        name: string;
    }[];
    selectedEmailSequenceTemplateId: string;
    addEmail: () => void;
    removeEmail?: (arg1: string) => void;
    onEmailSelect: (templateId: string) => void;
    onSubjectChange: (newSubject: string) => void;
    onBodyChange: (newHtml: string, textValue: string) => void;
    /** This is called whenever the body changes without being focused, to allow avoiding dirtying the form */
    onBodyLoad: (newHtml: string, textValue: string) => void;
    onDelayChange: (delay: Delay, templateId: string) => void;
    onSendAsReplyChange: () => void;
    onUseSignatureChange: () => void;
    onShowProInfo: () => void;
    onSelectEmailSequenceTemplate: (emailSequenceTemplateId?: string | null | undefined) => void;
    showEmailSequenceTemplateList: boolean;
    isUsersSequenceDirty: boolean;
    /*
    If we show placeholders, should we allow adding custom placeholders. We
    don't allow custom placeholders on email templates that are currently used
    for a stage automation subscription
    */
    allowAddingCustomPlaceholders: boolean;
};

type State = {
    isBodyFieldFocused: boolean;
    sortedTemplates: EmailTemplate[];
};

export class EmailTemplateDesignerEditor extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isBodyFieldFocused: false,
            sortedTemplates: sortByPosition(props.emailTemplates),
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.emailTemplates !== this.props.emailTemplates) {
            this.setState({sortedTemplates: sortByPosition(nextProps.emailTemplates)});
        }
    }

    render() {
        const readOnly = this.props.isLoading || this.props.readOnly;
        const canSendSequences = hasEmailSequenceCapability();
        const adderText = (
            <div className={styles['template-message-adder-button']}>
                <div>Add follow-up email</div>
                {!canSendSequences && <UpgradeArrowIcon size={16} />}
            </div>
        );
        const handleAdderClick = canSendSequences ? this.props.addEmail : this.props.onShowProInfo;
        const currentTemplate = this.getActiveTemplate();
        let editorStyleName = this.state.isBodyFieldFocused ? styles['body--focused'] : styles.body;
        if (readOnly) {
            editorStyleName = styles['body--read-only'];
        }

        // The current user's email sequence template
        const userEmailSequenceTemplate =
            this.props.sortedEmailSequenceTemplates &&
            this.props.sortedEmailSequenceTemplates.length
                ? this.props.sortedEmailSequenceTemplates[0]
                : null;
        // The sibling email sequence templates from the root
        const otherEmailSequenceTemplates =
            this.props.sortedEmailSequenceTemplates &&
            this.props.sortedEmailSequenceTemplates.length
                ? this.props.sortedEmailSequenceTemplates.slice(1)
                : [];

        const userEmailSequenceTemplateButtonStyles = classnames({
            [styles['current-user-sequence']]: true,

            [styles['current-user-sequence--selected']]:
                userEmailSequenceTemplate &&
                this.props.selectedEmailSequenceTemplateId === userEmailSequenceTemplate.id,

            [styles['current-user-sequence--blue']]:
                this.props.isUsersSequenceDirty ||
                (userEmailSequenceTemplate && userEmailSequenceTemplate.id === PLACEHOLDER_ID),

            [styles['current-user-sequence--blue-selected']]:
                userEmailSequenceTemplate &&
                this.props.selectedEmailSequenceTemplateId === userEmailSequenceTemplate.id &&
                (this.props.isUsersSequenceDirty ||
                    userEmailSequenceTemplate.id === PLACEHOLDER_ID),
        });

        return (
            <GetSessionUser>
                {({user}) => (
                    <div className={styles['email-template-designer-editor']}>
                        {this.props.isLoading ? (
                            <div className={styles['loading-overlay']} />
                        ) : null}
                        {hasEmailSequenceAutomation() &&
                        this.props.showEmailSequenceTemplateList ? (
                            <React.Fragment>
                                <div className={styles['user-sequence-list']}>
                                    {userEmailSequenceTemplate ? (
                                        <div className={userEmailSequenceTemplateButtonStyles}>
                                            <Button
                                                size='big'
                                                variant='text-secondary'
                                                onClick={() => {
                                                    this.props.onSelectEmailSequenceTemplate(
                                                        userEmailSequenceTemplate.id
                                                    );
                                                }}
                                            >
                                                {this.getUserEmailSequenceTemplateButtonText(
                                                    userEmailSequenceTemplate.id
                                                )}
                                            </Button>
                                        </div>
                                    ) : undefined}
                                    {otherEmailSequenceTemplates.length ? (
                                        <div className={styles['sequence-title-container']}>
                                            <SectionLabel>Team versions</SectionLabel>
                                        </div>
                                    ) : undefined}
                                    {/* @ts-expect-error */}
                                    <SelectableListSingle
                                        collection={otherEmailSequenceTemplates}
                                        alwaysHasValue={false}
                                        hasSearch={false}
                                        isNewStyle={true}
                                        value={this.props.selectedEmailSequenceTemplateId}
                                        onSelect={this.props.onSelectEmailSequenceTemplate}
                                    />
                                </div>
                                <div className={styles.divider} />
                            </React.Fragment>
                        ) : undefined}
                        <div className={styles['email-template-list']}>
                            <ListSequence
                                shouldShowCounts={Boolean(user && user.isEmailTrackingEnabled)}
                                onRemove={this.props.removeEmail}
                                keepingOnlyFirst={!canSendSequences}
                                collection={this.state.sortedTemplates}
                                selectedRowId={this.props.selectedTemplateId}
                                adder={
                                    readOnly
                                        ? undefined
                                        : {
                                              text: adderText,
                                              onClick: handleAdderClick,
                                          }
                                }
                                renderRow={this.renderRow}
                            />
                        </div>
                        <div className={styles['email-composer-area']}>
                            <div className={styles['subject-container']}>
                                <TextfieldWithCheckbox
                                    id='sendAsReply'
                                    name='sendAsReply'
                                    autoFocus={true}
                                    textfieldValue={this.getCurrentSubject()}
                                    onTextfieldChange={this.props.onSubjectChange}
                                    // @ts-expect-error
                                    disabled={this.getCurrentSendAsReply()}
                                    isReadOnly={readOnly}
                                    placeholder={this.props.isLoading ? '' : 'Add a subject…'}
                                    checkbox={
                                        this.isFirstTemplate()
                                            ? undefined
                                            : {
                                                  onChange: this.props.onSendAsReplyChange,
                                                  label: 'Send as reply',
                                                  checked: this.getCurrentSendAsReply(),
                                              }
                                    }
                                />
                                {currentTemplate && this.shouldShowCounts() ? (
                                    <React.Fragment>
                                        {
                                            // I'm not really 100% sure why this is necessary,
                                            // but without creating these tooltips, the sends
                                            // tooltip will display twice.
                                        }
                                        <ReactTooltip
                                            id='sends'
                                            effect='solid'
                                            className='react-tooltip'
                                        />
                                        <ReactTooltip
                                            id='opens'
                                            effect='solid'
                                            className='react-tooltip'
                                        />
                                        <a
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            href={this.getPeopleClickedTemplateLink()}
                                            data-tip={`${currentTemplate.clickCount.toLocaleString()} ${
                                                currentTemplate.clickCount === 1
                                                    ? 'email'
                                                    : 'emails'
                                            } clicked`}
                                            data-place='top'
                                            data-for='sends'
                                            className={styles['count-container--clickable']}
                                        >
                                            <div className={styles['view-icon-container']}>
                                                <LinkClickIcon size={24} />
                                            </div>
                                            {currentTemplate.clickCount.toLocaleString()}
                                        </a>
                                        <div className={styles['separator-bar']} />
                                        <a
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            href={this.getPeopleReceivedTemplateLink()}
                                            data-tip={`Sent ${currentTemplate.sentCount.toLocaleString()} ${
                                                currentTemplate.sentCount === 1 ? 'time' : 'times'
                                            }`}
                                            data-place='top'
                                            data-for='sends'
                                            className={styles['count-container--clickable']}
                                        >
                                            <div className={styles['view-icon-container']}>
                                                <SentEmailIcon size={24} />
                                            </div>
                                            {currentTemplate.sentCount.toLocaleString()}
                                        </a>
                                        <div className={styles['separator-bar']} />
                                        <a
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            href={this.getPeopleOpenedTemplateLink()}
                                            data-tip={`Opened ${currentTemplate.openedCount.toLocaleString()} ${
                                                currentTemplate.openedCount === 1 ? 'time' : 'times'
                                            }`}
                                            data-place='top'
                                            data-for='opens'
                                            className={styles['count-container--clickable']}
                                        >
                                            <div className={styles['view-icon-container']}>
                                                <EyeIcon size={18} />
                                            </div>
                                            {currentTemplate.openedCount.toLocaleString()}
                                        </a>
                                    </React.Fragment>
                                ) : undefined}
                            </div>
                            <div className={editorStyleName}>
                                {this.props.isLoading ? null : (
                                    <EmailEditor
                                        value={this.getCurrentBodyHTML()}
                                        placeholder={this.props.isLoading ? '' : 'Add a body…'}
                                        onFocus={() => {
                                            this.setState({isBodyFieldFocused: true});
                                        }}
                                        onBlur={() => {
                                            this.setState({isBodyFieldFocused: false});
                                        }}
                                        onChange={this.handleBodyChange}
                                        signature={user && user.emailSignature}
                                        useSignature={this.getCurrentUseSignature()}
                                        onChangeUseSignature={this.props.onUseSignatureChange}
                                        sendAsReply={this.getCurrentSendAsReply()}
                                        allowAddingPlaceholders={true}
                                        customPlaceholderClickAction={PLACEHOLDER_ACTION_INFO}
                                        allowAddingCustomPlaceholders={
                                            this.props.allowAddingCustomPlaceholders
                                        }
                                        readOnly={readOnly}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </GetSessionUser>
        );
    }

    getUserEmailSequenceTemplateButtonText = (userEmailSequenceTemplateId: string): string => {
        if (this.props.isUsersSequenceDirty) {
            return 'Your unsaved draft';
        }

        return userEmailSequenceTemplateId === PLACEHOLDER_ID
            ? 'Create your version'
            : 'Your version';
    };

    renderRow = (rowProps: {
        isSelected: boolean;
        shouldShowCounts: boolean;
        row: EmailTemplate;
        index: number;
    }) => {
        const {isSelected, shouldShowCounts, row: template, index} = rowProps;
        const {readOnly} = this.props;
        // The first template should not have a delay, all others do
        const hasDelay = index !== 0;

        const showProBadgeForRow = this.shouldShowProBadges() && index > 0;

        const subject = template.sendAsReply ? template.replySubject : template.subject;
        const title = subject || (this.props.isLoading ? '' : NO_SUBJECT);

        const linkCount = countHtmlAnchorElements(template.bodyHTML);

        const rowTitle =
            // @ts-expect-error
            this.shouldShowProBadges && showProBadgeForRow ? (
                <div className={styles['row-title-container']}>
                    <div>{title}</div>
                    <UpgradeArrow
                        featureName={'personal email sequences'}
                        // @ts-expect-error
                        size={16}
                        color={colors.blue}
                    />
                </div>
            ) : (
                title
            );

        return {
            iconOffset: hasDelay ? 50 : undefined,
            body: (
                <div className={styles['email-template-list-item']}>
                    {hasDelay ? (
                        <EmailDelay
                            delay={template.delay}
                            onTimespanChange={(timespan) => {
                                this.handleChangeDelayTimespan(timespan, template.id);
                            }}
                            readOnly={readOnly}
                        />
                    ) : null}
                    <ListSequenceRowBody
                        isSelected={isSelected}
                        onClick={this.handleRowSelect(template)}
                        title={rowTitle}
                        linkCount={linkCount}
                        shouldShowCounts={shouldShowCounts}
                        // Have to trim the bodyText, because quill adds a carriage return
                        subtitle={template.bodyText.trim() || (this.props.isLoading ? '' : NO_BODY)}
                        errorMessage={template.showError ? template.error : null}
                        includeReactTooltipComponent={false}
                    />
                </div>
            ),
        };
    };

    shouldShowProBadges() {
        return this.state.sortedTemplates.length > 1 && !hasEmailSequenceCapability();
    }

    handleBodyChange = (newHtml: string, textValue: string) => {
        if (this.state.isBodyFieldFocused) {
            this.props.onBodyChange(newHtml, textValue);
        } else {
            this.props.onBodyLoad(newHtml, textValue);
        }
    };

    isFirstTemplate(): boolean {
        const firstTemplate = sortByPosition(this.props.emailTemplates)[0];

        if (firstTemplate) {
            return firstTemplate.id === this.props.selectedTemplateId;
        }

        return true;
    }

    getActiveTemplate = (): EmailTemplate | null | undefined => {
        return this.props.emailTemplates[this.props.selectedTemplateId];
    };

    shouldShowCounts = (): boolean => {
        let shouldShowCounts = false;

        Object.keys(this.props.emailTemplates).forEach((key) => {
            // @ts-expect-error
            if (this.props.emailTemplates[key].sentCount > 0) {
                shouldShowCounts = true;
            }
        });

        return shouldShowCounts;
    };

    getActiveTemplateId(): string | null | undefined {
        const activeTemplate = this.getActiveTemplate();

        return activeTemplate ? activeTemplate.id : null;
    }

    getCurrentSubject = () => {
        const template = this.getActiveTemplate();
        if (template) {
            return template.sendAsReply ? template.replySubject : template.subject;
        }

        return '';
    };

    getCurrentBodyHTML = () => {
        const template = this.getActiveTemplate();
        if (template) {
            return template.bodyHTML;
        }

        return '';
    };

    getCurrentSendAsReply = () => {
        const template = this.getActiveTemplate();
        if (template) {
            return template.sendAsReply;
        }

        return true;
    };

    getCurrentUseSignature = () => {
        const template = this.getActiveTemplate();
        if (template) {
            return template.useSignature;
        }

        return true;
    };

    handleChangeDelayTimespan = (timespan: string | null | undefined, templateId: string) => {
        // Don't allow clearing a delay completely
        if (!timespan) return;

        const newDelay = {
            timespan: Number(timespan),
            unit: 'bzd', // This may be configurable in the future, but isn't now.
        } as const;
        this.props.onDelayChange(newDelay, templateId);
    };

    handleRowSelect = (template: EmailTemplate) => () => {
        this.props.onEmailSelect(template.id);
    };

    getPeopleReceivedTemplateLink = () => {
        const selectedEmailSequenceTemplateId = this.props.selectedEmailSequenceTemplateId;
        const filter = selectedEmailSequenceTemplateId
            ? Routing.param({
                  fields: {
                      contacts: 'name,lastContactedTime,email,receivedEmailSequenceTemplates',
                  },
                  filter: {
                      receivedEmailSequenceTemplates: {
                          data: [
                              {
                                  data: selectedEmailSequenceTemplateId,
                              },
                          ],
                      },
                  },
              })
            : null;

        return filter ? `/people?${filter}&persist=false` : undefined;
    };

    getPeopleClickedTemplateLink = () => {
        const selectedEmailSequenceTemplateId = this.props.selectedEmailSequenceTemplateId;
        const filter = selectedEmailSequenceTemplateId
            ? Routing.param({
                  fields: {
                      contacts:
                          'name,lastContactedTime,email,emailOpenCount,emailClickCount,openedEmailSequenceTemplates,clickedEmailSequenceTemplates',
                  },
                  filter: {
                      clickedEmailSequenceTemplates: {
                          data: [
                              {
                                  data: selectedEmailSequenceTemplateId,
                              },
                          ],
                      },
                  },
              })
            : null;

        return filter ? `/people?${filter}&persist=false` : undefined;
    };

    getPeopleOpenedTemplateLink = () => {
        const selectedEmailSequenceTemplateId = this.props.selectedEmailSequenceTemplateId;
        const filter = selectedEmailSequenceTemplateId
            ? Routing.param({
                  fields: {
                      contacts:
                          'name,lastContactedTime,email,emailOpenCount,emailClickCount,openedEmailSequenceTemplates,clickedEmailSequenceTemplates',
                  },
                  filter: {
                      openedEmailSequenceTemplates: {
                          data: [
                              {
                                  data: selectedEmailSequenceTemplateId,
                              },
                          ],
                      },
                  },
              })
            : null;

        return filter ? `/people?${filter}&persist=false` : undefined;
    };
}
